import { useAuth } from '../contexts/AuthContext'
import { API_BASE_URL } from '../config'

// Per-tenant vocabulary override for the tenant's own dashboard (e.g.
// "Client" -> "Contact") — see TerminologyContext.tsx. All keys optional;
// unset/blank falls back to the standard English word. Deliberately never
// read by the client-facing portal (ClientIntakePage.tsx/PortalPage.tsx) —
// see that context's module comment for why.
export interface TenantTerminology {
  clientSingular?: string;
  clientPlural?: string;
  matterSingular?: string;
  matterPlural?: string;
  taskSingular?: string;
  taskPlural?: string;
}

export interface TenantDetails {
  id: string;
  email: string;
  createdAt: string;
  merchantId?: string;
  merchantType?: string;
  companyName?: string;
  // The tenant's own display name — shown as "Requested by" on the public
  // signer view instead of a bare email (see backend/src/esign.ts).
  name?: string;
  // Optional signer-page branding — see BRAND_FONT_OPTIONS in
  // backend/src/index.ts for the fontFamily allowlist. Blank/unset falls
  // back to Conveyo.One's own default look (see SignPage.tsx).
  logoUrl?: string;
  // Set instead of logoUrl by uploadLogo below — an internal pointer, never
  // used directly for display (see TenantResponse.logoDownloadUrl).
  logoS3Key?: string;
  brandColor?: string;
  fontFamily?: string;
  payerId?: string;
  planKey?: string;
  subscriptionId?: string;
  terminology?: TenantTerminology;
}

export const BRAND_FONT_OPTIONS = ['Roboto', 'Lora', 'Merriweather', 'Poppins', 'Playfair Display', 'Montserrat'] as const;

export interface TenantResponse {
  tenant: TenantDetails;
  hasMerchantId: boolean;
  hasMerchantType: boolean;
  isMerchantConfigured: boolean;
  // Presigned GET for tenant.logoS3Key when an uploaded logo is set — null
  // otherwise (including when the tenant used the plain logoUrl field
  // instead, which the frontend can just render directly).
  logoDownloadUrl: string | null;
}

export interface ManagedMerchantContact {
  isPrimaryContact: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  streetAddress: string;
  suburb: string;
  postcode: string;
  state: string;
  country: string;
  dob: string;
  contactType: string;
  ownership: number;
  jobTitle: string;
}

export interface CreateManagedMerchantPayload {
  planKey?: string;
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  companyWebsiteUrl: string;
  companyRegistrationNumber: string;
  bankAccountName: string;
  bankAccountRoutingNumber: string;
  bankAccountNumber: string;
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  natureOfBusiness: string;
  organisationType: string;
  contacts: ManagedMerchantContact[];
}

export const useTenantService = () => {
  const { token, authFetch } = useAuth()

  const fetchTenantDetails = async (): Promise<TenantResponse | null> => {
    if (!token) {
      return null
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/tenant-details`)

      if (!response.ok) {
        throw new Error(`Failed to fetch tenant details: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching tenant details:', error)
      return null
    }
  }

  const createManagedMerchant = async (payload: CreateManagedMerchantPayload): Promise<TenantResponse> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/create-managed-merchant`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to create managed merchant: ${response.status}`)
    }

    return data
  }

  // Partial — only pass the fields you want to change (e.g. { name } or
  // { logoUrl, brandColor, fontFamily }); an empty string clears a
  // previously-set branding field. `terminology`, when passed, always
  // replaces the whole map — send every key from the Settings form, not
  // just the one the tenant edited (see PATCH /tenant-details in index.ts).
  const updateTenantProfile = async (patch: Partial<Pick<TenantDetails, 'name' | 'logoUrl' | 'brandColor' | 'fontFamily' | 'terminology'>>): Promise<TenantResponse> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/tenant-details`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(patch),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to update profile: ${response.status}`)
    }

    return data
  }

  // Uploads go straight to S3 via a presigned URL rather than through the
  // Lambda (same pattern as documentService.ts's uploadDocument) — supersedes
  // any logoUrl previously set via updateTenantProfile (see index.ts).
  const uploadLogo = async (file: File): Promise<TenantResponse> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const startResponse = await authFetch(`${API_BASE_URL}/tenant-details/logo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream' }),
    })
    const startData = await startResponse.json()

    if (!startResponse.ok) {
      throw new Error(startData?.error || `Failed to start logo upload: ${startResponse.status}`)
    }

    const uploadResponse = await fetch(startData.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    })

    if (!uploadResponse.ok) {
      throw new Error(`Logo upload failed: ${uploadResponse.status}`)
    }

    const { uploadUrl, ...tenantResponse } = startData
    void uploadUrl
    return tenantResponse
  }

  return { fetchTenantDetails, createManagedMerchant, updateTenantProfile, uploadLogo }
}